// ============================================================================
// THE DESIGNED DAY PAGE'S WORDS AND RULES — pure functions, so a test can read every one.
//
// docs/design/handoff/day-page/ is the source: README.md (owner decisions, closed) and the boards.
// Nothing here reads the wall clock — "now" is the app clock (`useClock`), fixed in demo mode — and
// nothing here invents a field the API does not carry.
//
// The prototype's words (`model.ts`) stay as they were: the phone still shows the prototype.
// ============================================================================

import {
  addDays,
  daysBetween,
  formatShortDate,
  minutesToClock,
  startOfWeek,
  weekdayName,
  type Assignment,
  type ISODate,
  type Task,
} from '@app/shared'

import { clockInstant, localDayOf, localMinutesOfDay } from '../../lib/time'
import type { MailTriageAccount, MailTriageItem, MailVerdict } from '../../lib/data/types'

// ── Piles ───────────────────────────────────────────────────────────────────

/** Keys 1–4, in this order. The API value `can_wait` stays; its name is "Low priority" (owner, 2026-10-06). */
export const PILE_ORDER: MailVerdict[] = ['needs_you_today', 'important', 'can_wait', 'noise']

/** The pile as a heading, a folded row and a toast. */
export const PILE_NAME: Record<MailVerdict, string> = {
  needs_you_today: 'Needs you today',
  important: 'Important',
  can_wait: 'Low priority',
  noise: 'Noise',
}

/** The pile on the segmented control (Main board: "1 Today · 2 Important · 3 Low priority · 4 Noise"). */
export const PILE_SEG: Record<MailVerdict, string> = {
  needs_you_today: 'Today',
  important: 'Important',
  can_wait: 'Low priority',
  noise: 'Noise',
}

/** The pile inside a sentence ("sorted as noise by Claude · you moved it to important"). */
export const PILE_WORD: Record<MailVerdict, string> = {
  needs_you_today: 'needs you today',
  important: 'important',
  can_wait: 'low priority',
  noise: 'noise',
}

/** What each pile's "move here" tooltip says (Main board). The first is not drawn; it follows the pattern. */
export const PILE_MOVE_TIP: Record<MailVerdict, string> = {
  needs_you_today: "Back with today's work. Corrects Claude's sorting.",
  important: "Worth seeing, nothing to do yet. Corrects Claude's sorting.",
  can_wait: "Folded away below. Corrects Claude's sorting.",
  noise: "Folded away below, still searchable. Corrects Claude's sorting.",
}

export const REASON_CHIPS = [
  { reason: 'person', label: 'person' },
  { reason: 'deadline', label: 'deadline' },
  { reason: 'money', label: 'money' },
  { reason: 'not_interested', label: 'not interested' },
  { reason: 'other', label: 'other' },
] as const

/** "Where Claude sorted it. Press 2–4 to move it." — the pressed segment's tooltip. */
export function currentPileTip(item: Pick<MailTriageItem, 'verdict' | 'ownerVerdict' | 'triageSource'>): string {
  const at = item.verdict ? PILE_ORDER.indexOf(item.verdict) + 1 : 0
  const others = [1, 2, 3, 4].filter((n) => n !== at)
  const keys =
    others[0]! + 1 === others[1] && others[1]! + 1 === others[2]
      ? `${others[0]}–${others[2]}`
      : `${others[0]}, ${others[1]} or ${others[2]}`
  const who = item.ownerVerdict ? 'Where you moved it.' : item.triageSource === 'rule' ? 'Where your rule sorted it.' : 'Where Claude sorted it.'
  return `${who} Press ${keys} to move it.`
}

// ── Inboxes ─────────────────────────────────────────────────────────────────

export type InboxName = 'Professional' | 'Berkeley' | 'Flexible'

/**
 * WHICH INBOX — the Worker's own rule, mirrored (apps/worker/src/lib/mail-triage.ts → inboxRole):
 * a Berkeley address is the school inbox, the one flexible address is the flexible inbox, and
 * everything else is professional. The API does not send the role, so the page reads it the same
 * way the sorter does; the demo dataset's `.example` stand-ins follow the same three roles.
 */
const SCHOOL_DOMAINS = ['berkeley.edu', 'school.example']
const FLEXIBLE_INBOXES = ['yasenmind@gmail.com', 'kaz@personal.example']

export function inboxName(address: string): InboxName {
  const a = address.trim().toLowerCase()
  const domain = a.split('@')[1] ?? ''
  if (SCHOOL_DOMAINS.some((d) => domain === d || domain.endsWith(`.${d}`))) return 'Berkeley'
  if (FLEXIBLE_INBOXES.includes(a)) return 'Flexible'
  return 'Professional'
}

const INBOX_ORDER: InboxName[] = ['Professional', 'Berkeley', 'Flexible']

/** Professional, Berkeley, Flexible — the order the board's freshness chips read in. */
export function orderAccounts<T extends { address: string }>(accounts: T[]): T[] {
  return [...accounts].sort((a, b) => INBOX_ORDER.indexOf(inboxName(a.address)) - INBOX_ORDER.indexOf(inboxName(b.address)))
}

// ── Times ───────────────────────────────────────────────────────────────────

/** '6:42' · '22:10' — the board's freshness and provenance times (24-hour, no meridiem). */
export function clock24(ts: string): string {
  const minutes = localMinutesOfDay(ts)
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`
}

/** '6:42' today, 'yesterday 22:10', 'Sep 14 22:10'. */
export function dayClock24(ts: string, today: ISODate): string {
  const day = localDayOf(ts)
  if (day === today) return clock24(ts)
  if (day === addDays(today, -1)) return `yesterday ${clock24(ts)}`
  return `${formatShortDate(day)} ${clock24(ts)}`
}

/** '7:31 AM' — an event, a deadline, a snooze. */
export function clock12(ts: string): string {
  return minutesToClock(localMinutesOfDay(ts))
}

/** 'Thu Oct 8' — the Coming up eyebrow and the folded rows' day. */
export function dayLabel(date: ISODate): string {
  return `${weekdayName(date, true)} ${formatShortDate(date)}`
}

/** An instant for a local day and minute, in this device's zone. */
export function localInstant(date: ISODate, minutes: number): string {
  return new Date(clockInstant(date, minutes)).toISOString()
}

// ── Sources (14e / 14f) ─────────────────────────────────────────────────────

export type SourceState = 'fresh' | 'stale' | 'disconnected' | 'loading'

export interface SourceChip {
  key: string
  name: string
  state: SourceState
  /** '6:42' · 'as of yesterday 22:10' · 'disconnected' · 'loading' — the text after the name. */
  value: string
  /** What the stale or disconnected banner says, in the Worker's own words. */
  error: string | null
  lastSyncAt: string | null
}

/** The sentence the Worker writes onto an account Google revoked (apps/worker/src/lib/gmail-adapter.ts). */
export const GMAIL_RECONNECT = 'Google no longer accepts this connection. Disconnect and connect it again.'

/** Six missed 30-minute syncs. Past this, a source is stale even if nothing said it failed. */
export const STALE_AFTER_MINUTES = 180

export function sourceState(
  lastSyncAt: string | null,
  lastError: string | null,
  today: ISODate,
  nowMinutes: number,
): Exclude<SourceState, 'loading'> {
  if (lastError && lastError.startsWith('Google no longer accepts')) return 'disconnected'
  if (lastError) return 'stale'
  if (!lastSyncAt) return 'stale'
  const age = (clockInstant(today, nowMinutes) - Date.parse(lastSyncAt)) / 60_000
  return age > STALE_AFTER_MINUTES ? 'stale' : 'fresh'
}

export function mailSourceChip(account: MailTriageAccount, today: ISODate, nowMinutes: number): SourceChip {
  const name = inboxName(account.address)
  const state = sourceState(account.lastSyncAt, account.lastError, today, nowMinutes)
  const value =
    state === 'disconnected'
      ? 'disconnected'
      : !account.lastSyncAt
        ? 'not synced yet'
        : state === 'stale'
          ? `as of ${dayClock24(account.lastSyncAt, today)}`
          : clock24(account.lastSyncAt)
  return { key: account.id, name, state, value, error: account.lastError, lastSyncAt: account.lastSyncAt }
}

export function connectorChip(
  key: string,
  name: string,
  lastSyncAt: string | null,
  failing: boolean,
  today: ISODate,
  nowMinutes: number,
): SourceChip {
  const state = failing ? 'stale' : sourceState(lastSyncAt, null, today, nowMinutes)
  const value = !lastSyncAt ? 'not synced yet' : state === 'stale' ? `as of ${dayClock24(lastSyncAt, today)}` : clock24(lastSyncAt)
  return { key, name, state, value, error: null, lastSyncAt }
}

/** '2 sources need attention' — the top bar's chip when anything is stale or gone. */
export function attentionLine(chips: SourceChip[]): string | null {
  const n = chips.filter((c) => c.state === 'stale' || c.state === 'disconnected').length
  if (n === 0) return null
  return n === 1 ? '1 source needs attention' : `${n} sources need attention`
}

// ── Provenance ──────────────────────────────────────────────────────────────

/** 'Claude' for every Anthropic model id; the raw id otherwise. Never blank. */
export function modelName(model: string | null): string {
  if (!model) return 'the model'
  return /claude|haiku|sonnet|opus/i.test(model) ? 'Claude' : model
}

/** 'sorted by Claude at 6:42' · 'sorted by Claude yesterday at 22:14' · 'sorted by your rule'. */
export function sortedBy(item: Pick<MailTriageItem, 'triageSource' | 'triageModel' | 'triagedAt'>, today: ISODate): string | null {
  if (item.triageSource === 'rule') return 'sorted by your rule'
  if (item.triageSource !== 'model') return null
  const who = `sorted by ${modelName(item.triageModel)}`
  if (!item.triagedAt) return who
  const day = localDayOf(item.triagedAt)
  if (day === today) return `${who} at ${clock24(item.triagedAt)}`
  if (day === addDays(today, -1)) return `${who} yesterday at ${clock24(item.triagedAt)}`
  return `${who} ${formatShortDate(day)} at ${clock24(item.triagedAt)}`
}

/**
 * THE MOVED CARD'S TWO HALVES (Components board): "sorted as noise by Claude" and "· you moved it
 * to important". Null when the owner never moved it — or when there is no model verdict to set
 * against his (a rule's sort is his own already).
 */
export function movedLine(item: Pick<MailTriageItem, 'ownerVerdict' | 'modelVerdict' | 'triageModel'>): { was: string; now: string } | null {
  if (!item.ownerVerdict || !item.modelVerdict || item.ownerVerdict === item.modelVerdict) return null
  return {
    was: `sorted as ${PILE_WORD[item.modelVerdict]} by ${modelName(item.triageModel)}`,
    now: `· you moved it to ${PILE_WORD[item.ownerVerdict]}`,
  }
}

// ── Mail card lines ─────────────────────────────────────────────────────────

/** 'Rowan · festival.example' — the sender as the card's source line starts. */
export function senderLine(item: Pick<MailTriageItem, 'from'>): string {
  const domain = item.from.address.split('@')[1] ?? ''
  const name = item.from.name?.trim()
  return name ? `${name} · ${domain}` : item.from.address
}

/** The short sender of a folded row and the fold summary: the name, else the address. */
export function senderShort(item: Pick<MailTriageItem, 'from'>): string {
  return item.from.name?.trim() || item.from.address
}

/** 'first seen Sep 30' for a carried-over card; the arrival ('7:31 AM' today, 'Oct 2' earlier) otherwise. */
export function seenLine(item: Pick<MailTriageItem, 'ageDays' | 'firstSurfacedOn' | 'receivedAt'>, today: ISODate): string {
  if (item.ageDays > 0 && item.firstSurfacedOn) return `first seen ${formatShortDate(item.firstSurfacedOn)}`
  const day = localDayOf(item.receivedAt)
  return day === today ? clock12(item.receivedAt) : formatShortDate(day)
}

/** The right-hand time: 'due today' (urgent), 'Tue Oct 6' (quiet), or nothing. */
export function mailWhen(item: Pick<MailTriageItem, 'deadline'>, today: ISODate): { text: string; urgent: boolean } | null {
  if (!item.deadline) return null
  if (item.deadline === today) return { text: 'due today', urgent: true }
  if (item.deadline < today) return { text: `was due ${formatShortDate(item.deadline)}`, urgent: true }
  return { text: dayLabel(item.deadline), urgent: false }
}

/** 'sign-in alert · stays until handled' / 'clears itself tomorrow' — the two pills the board draws. */
export function mailPill(item: Pick<MailTriageItem, 'security' | 'isCode'>): { text: string; tone: 'warn' | 'quiet' } | null {
  if (item.security) return { text: 'sign-in alert · stays until handled', tone: 'warn' }
  if (item.isCode) return { text: 'clears itself tomorrow', tone: 'quiet' }
  return null
}

// ── Carry-over ──────────────────────────────────────────────────────────────

/** '1 day' · '12 days'. */
export function ageLabel(days: number): string {
  return `${days} ${days === 1 ? 'day' : 'days'}`
}

/** "1 day is quiet; 2 days or more turns orange. No red, nothing expires." (Components board) */
export function ageTone(days: number): 'quiet' | 'orange' {
  return days >= 2 ? 'orange' : 'quiet'
}

// ── Folded piles ────────────────────────────────────────────────────────────

/**
 * 'Amazon 4 · Stripe · AGI House · L&S Events · 2 more' — senders by how many they sent, a count
 * only when it is more than one, as many as fit the row, then how many MESSAGES are left.
 */
export function foldSummary(items: Pick<MailTriageItem, 'from' | 'receivedAt'>[], budget = 48): string {
  const groups = new Map<string, { n: number; latest: number }>()
  for (const item of items) {
    const name = senderShort(item)
    const g = groups.get(name) ?? { n: 0, latest: 0 }
    g.n += 1
    g.latest = Math.max(g.latest, Date.parse(item.receivedAt))
    groups.set(name, g)
  }
  const ordered = [...groups.entries()].sort((a, b) => b[1].n - a[1].n || b[1].latest - a[1].latest)
  const parts: string[] = []
  let shown = 0
  for (const [name, g] of ordered) {
    const part = g.n > 1 ? `${name} ${g.n}` : name
    const next = [...parts, part].join(' · ')
    if (parts.length > 0 && next.length > budget) break
    parts.push(part)
    shown += g.n
  }
  const left = items.length - shown
  return left > 0 ? `${parts.join(' · ')} · ${left} more` : parts.join(' · ')
}

/** 'back tonight, 8:00 PM' · 'back tomorrow, 8:00 AM' · 'back Mon Sep 21, 8:00 AM'. */
export function backLabel(until: string, today: ISODate): string {
  return `back ${whenPhrase(until, today)}`
}

/** 'tonight, 8:00 PM' · 'today, 2:00 PM' · 'tomorrow, 8:00 AM' · 'Mon Sep 21, 8:00 AM'. */
export function whenPhrase(until: string, today: ISODate): string {
  const day = localDayOf(until)
  const minutes = localMinutesOfDay(until)
  const time = minutesToClock(minutes)
  if (day === today) return `${minutes >= 17 * 60 ? 'tonight' : 'today'}, ${time}`
  if (day === addDays(today, 1)) return `tomorrow, ${time}`
  return `${dayLabel(day)}, ${time}`
}

// ── Snooze ──────────────────────────────────────────────────────────────────

export interface SnoozeChoice {
  key: 1 | 2 | 3
  label: string
  /** The menu's right-hand detail: '8:00 PM' · 'Tue 8:00 AM' · 'Mon Oct 12, 8:00 AM'. */
  detail: string
  until: string
}

/**
 * THE THREE PRESETS (owner, closed): Tonight 8:00 PM · Tomorrow 8:00 AM · Next week Mon 8:00 AM.
 * Tonight only while 8 PM is still ahead. "Next week" is always a Monday AFTER today — on a
 * Monday it is a week out.
 */
export function snoozeChoices(today: ISODate, nowMinutes: number): SnoozeChoice[] {
  const out: SnoozeChoice[] = []
  if (nowMinutes < 20 * 60) out.push({ key: 1, label: 'Tonight', detail: '8:00 PM', until: localInstant(today, 20 * 60) })
  const tomorrow = addDays(today, 1)
  out.push({ key: 2, label: 'Tomorrow morning', detail: `${weekdayName(tomorrow, true)} 8:00 AM`, until: localInstant(tomorrow, 8 * 60) })
  const monday = nextMonday(today)
  out.push({ key: 3, label: 'Next week', detail: `${dayLabel(monday)}, 8:00 AM`, until: localInstant(monday, 8 * 60) })
  return out
}

export function nextMonday(today: ISODate): ISODate {
  const back = startOfWeek(today)
  return addDays(back, 7)
}

export type SnoozeTarget = { kind: 'mail'; receivedAt: string } | { kind: 'reminder' } | { kind: 'assignment' }

/**
 * A DATE PICKED FROM THE CALENDAR (key C). Mail comes back at the time of day it ORIGINALLY
 * ARRIVED; a reminder or an assignment, which did not arrive by mail, at 8:00 AM.
 */
export function pickedSnoozeUntil(date: ISODate, target: SnoozeTarget): string {
  const minutes = target.kind === 'mail' ? localMinutesOfDay(target.receivedAt) : 8 * 60
  return localInstant(date, minutes)
}

/** 'Thu, Oct 8 at 4:26 PM' — the picker's footer. */
export function pickedPhrase(until: string): string {
  const day = localDayOf(until)
  return `${weekdayName(day, true)}, ${formatShortDate(day)} at ${clock12(until)}`
}

/** The picker's month: Monday-first weeks, padded with the neighbouring months' days. */
export function monthGrid(month: ISODate): { date: ISODate; inMonth: boolean }[] {
  const first = `${month.slice(0, 7)}-01` as ISODate
  const nextFirst = `${addDays(first, 32).slice(0, 7)}-01` as ISODate
  const last = addDays(nextFirst, -1)
  const end = addDays(startOfWeek(last), 6)
  const out: { date: ISODate; inMonth: boolean }[] = []
  for (let d = startOfWeek(first); d <= end; d = addDays(d, 1)) {
    out.push({ date: d, inMonth: d.slice(0, 7) === first.slice(0, 7) })
  }
  return out
}

// ── The other two kinds: bCourses and Reminders ─────────────────────────────

export interface DueToday {
  assignment: Assignment
  minutes: number | null
}

/** Open assignments due on `today` (not submitted). */
export function assignmentsDueToday(assignments: Assignment[], today: ISODate): DueToday[] {
  return assignments
    .filter((a) => a.dueAt && (a.status === 'upcoming' || a.status === 'missing') && localDayOf(a.dueAt) === today)
    .map((a) => ({ assignment: a, minutes: localMinutesOfDay(a.dueAt!) }))
    .sort((x, y) => (x.minutes ?? 0) - (y.minutes ?? 0))
}

/** "Due tonight. Not submitted on bCourses." — said only from what the assignment carries. */
export function assignmentWhy(minutes: number | null): string {
  return minutes !== null && minutes >= 18 * 60 ? 'Due tonight. Not submitted on bCourses.' : 'Due today. Not submitted on bCourses.'
}

export interface Waiting {
  key: string
  kind: 'assignment' | 'reminder'
  title: string
  ageDays: number
  /** The board's why line, or null where it draws none (reminders). */
  why: string | null
  source: string[]
  url: string | null
  task?: Task
  assignment?: Assignment
}

/**
 * STILL WAITING — from earlier days, oldest first, nothing expires: bCourses work past due and
 * still not submitted, and reminders whose day has passed.
 */
export function stillWaiting(assignments: Assignment[], tasks: Task[], today: ISODate): Waiting[] {
  const out: Waiting[] = []
  for (const a of assignments) {
    if (a.status !== 'missing' || !a.dueAt) continue
    const day = localDayOf(a.dueAt)
    if (day >= today) continue
    out.push({
      key: `assignment:${a.id}`,
      kind: 'assignment',
      title: a.title,
      ageDays: daysBetween(day, today),
      why: `Past due since ${formatShortDate(day)} and still open on bCourses.`,
      source: [a.courseCode, 'bCourses', `first seen ${formatShortDate(day)}`],
      url: a.htmlUrl,
      assignment: a,
    })
  }
  for (const t of tasks) {
    if (t.done || !t.due || t.due >= today) continue
    out.push({
      key: `reminder:${t.id}`,
      kind: 'reminder',
      title: t.title,
      ageDays: daysBetween(t.due, today),
      why: null,
      source: ['Reminders', `was due ${formatShortDate(t.due)}`],
      url: null,
      task: t,
    })
  }
  return out.sort((a, b) => b.ageDays - a.ageDays || a.title.localeCompare(b.title))
}

/** Reminders due today, not done. */
export function remindersDueToday(tasks: Task[], today: ISODate): Task[] {
  return tasks.filter((t) => !t.done && t.due === today)
}

/** Open reminders with no day at all. */
export function somedayCount(tasks: Task[]): number {
  return tasks.filter((t) => !t.done && t.due === null).length
}

// ── Coming up ───────────────────────────────────────────────────────────────

export interface ComingDay {
  date: ISODate
  rows: { key: string; title: string; course: string; time: string | null; url: string | null }[]
}

/** The next 7 days after today, bCourses work grouped by day. */
export function comingUp(assignments: Assignment[], today: ISODate): { days: ComingDay[]; count: number; to: ISODate; quietTail: [ISODate, ISODate] | null } {
  const to = addDays(today, 7)
  const byDay = new Map<ISODate, ComingDay>()
  for (const a of assignments) {
    if (!a.dueAt || a.status !== 'upcoming') continue
    const day = localDayOf(a.dueAt)
    if (day <= today || day > to) continue
    const minutes = localMinutesOfDay(a.dueAt)
    const row = { key: a.id, title: a.title, course: a.courseCode, time: minutesToClock(minutes), url: a.htmlUrl, minutes }
    const d = byDay.get(day) ?? { date: day, rows: [] }
    d.rows.push(row)
    byDay.set(day, d)
  }
  const days = [...byDay.values()].sort((a, b) => (a.date < b.date ? -1 : 1))
  for (const d of days) d.rows.sort((a, b) => ((a as { minutes?: number }).minutes ?? 0) - ((b as { minutes?: number }).minutes ?? 0))
  const last = days.at(-1)?.date ?? today
  const quietTail: [ISODate, ISODate] | null = last < to ? [addDays(last, 1), to] : null
  return { days, count: days.reduce((n, d) => n + d.rows.length, 0), to, quietTail }
}

/** 'Nothing due Sat Oct 10 – Mon Oct 12' · 'Nothing due Mon Oct 12'. */
export function quietTailLine([from, to]: [ISODate, ISODate]): string {
  return from === to ? `Nothing due ${dayLabel(from)}` : `Nothing due ${dayLabel(from)} – ${dayLabel(to)}`
}

// ── The header ──────────────────────────────────────────────────────────────

/** '4 need you · 3 not sorted yet · 4 still waiting · 2 events' — the band's quiet counts. */
export function headerSummary(n: { needs: number; unsorted: number; waiting: number; events: number; noInboxes?: boolean }): string {
  const parts: string[] = []
  if (n.noInboxes) parts.push('no inboxes connected yet')
  else parts.push(n.needs === 0 ? 'nothing needs you' : n.needs === 1 ? '1 needs you' : `${n.needs} need you`)
  if (n.unsorted > 0) parts.push(`${n.unsorted} not sorted yet`)
  if (n.waiting > 0) parts.push(`${n.waiting} still waiting`)
  parts.push(n.events === 0 ? 'no events' : n.events === 1 ? '1 event' : `${n.events} events`)
  return parts.join(' · ')
}

/** "3 mail · 1 bCourses" — what the Needs you today section is made of. */
export function needsMix(mail: number, bcourses: number, reminders: number): string {
  const parts: string[] = []
  if (mail) parts.push(`${mail} mail`)
  if (bcourses) parts.push(`${bcourses} bCourses`)
  if (reminders) parts.push(`${reminders} ${reminders === 1 ? 'reminder' : 'reminders'}`)
  return parts.join(' · ')
}

/** "All three inboxes were sorted at 7:10 and nothing asks for you today." */
export function quietLine(inboxes: number, sortedAt: string | null, waiting: number): string {
  const who = inboxes === 3 ? 'All three inboxes' : inboxes === 2 ? 'Both inboxes' : 'The inbox'
  const sorted = sortedAt ? `${inboxes === 1 ? 'was' : 'were'} sorted at ${clock24(sortedAt)} and nothing asks for you today.` : 'have nothing that asks for you today.'
  const tail = waiting === 0 ? ' Nothing from earlier days is still open.' : ''
  return `${who} ${sorted}${tail}`
}

// ── The weekly check ────────────────────────────────────────────────────────

/** The Monday of LAST week — the week the check is about. */
export function auditWeekStart(today: ISODate): ISODate {
  return addDays(startOfWeek(today), -7)
}

/** 'Sep 28 – Oct 4'. */
export function weekRange(weekStart: ISODate, weekEnd: ISODate): string {
  return `${formatShortDate(weekStart)} – ${formatShortDate(weekEnd)}`
}

/** 'Always important from kelsey@…' — a rule as a sentence. */
export function ruleSentence(rule: { verdict: MailVerdict; matchValue: string }): string {
  return `Always ${PILE_WORD[rule.verdict]} from ${rule.matchValue}`
}

// ── Keyboard ────────────────────────────────────────────────────────────────

/** True when a key press belongs to a text field, not to the page. */
export function typingInto(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  const tag = target.tagName
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (tag === 'INPUT') {
    const type = (target as HTMLInputElement).type
    return !['button', 'checkbox', 'radio', 'submit', 'reset'].includes(type)
  }
  return false
}
